import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useLanguageFromPath } from '../../hooks/useLanguageFromPath';
import { lp, getLangFromPath } from '../../utils/routes';
import { useLocation } from 'react-router-dom';
import { KAILAC_WHATSAPP_NUMBER } from '@/config/contactPhone';

type CategoryKey = 'all' | 'getting_started' | 'process' | 'plans_fees' | 'eligibility' | 'compliance' | 'bilingual';

const CATEGORY_KEYS: CategoryKey[] = [
  'all', 'getting_started', 'process', 'plans_fees', 'eligibility', 'compliance', 'bilingual',
];

const CATEGORY_META: Record<CategoryKey, { icon: string; accent: string; bg: string }> = {
  all: { icon: 'ri-apps-2-line', accent: 'text-[#4F2D63]', bg: 'bg-[#F8F3FB]' },
  getting_started: { icon: 'ri-rocket-2-line', accent: 'text-emerald-700', bg: 'bg-[#F0FDF9]' },
  process: { icon: 'ri-flow-chart', accent: 'text-amber-700', bg: 'bg-amber-50' },
  plans_fees: { icon: 'ri-price-tag-3-line', accent: 'text-[#4F2D63]', bg: 'bg-[#F8F3FB]' },
  eligibility: { icon: 'ri-user-check-line', accent: 'text-emerald-700', bg: 'bg-[#F0FDF9]' },
  compliance: { icon: 'ri-shield-check-line', accent: 'text-amber-700', bg: 'bg-amber-50' },
  bilingual: { icon: 'ri-translate-2', accent: 'text-[#4F2D63]', bg: 'bg-[#F8F3FB]' },
};

interface FaqItem {
  catKey: Exclude<CategoryKey, 'all'>;
  q: string;
  a: string;
}

export default function FAQPage() {
  useLanguageFromPath();
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const [activeCatKey, setActiveCatKey] = useState<CategoryKey>('all');
  const [search, setSearch] = useState('');

  const categoryLabels: Record<CategoryKey, string> = {
    all: t('faq_page_cat_all'),
    getting_started: t('faq_page_cat_getting_started'),
    process: t('faq_page_cat_process'),
    plans_fees: t('faq_page_cat_plans_fees'),
    eligibility: t('faq_page_cat_eligibility'),
    compliance: t('faq_page_cat_compliance'),
    bilingual: t('faq_page_cat_bilingual'),
  };

  const faqs: FaqItem[] = [
    { catKey: 'getting_started', q: t('faq_page_item_1_q'), a: t('faq_page_item_1_a') },
    { catKey: 'getting_started', q: t('faq_page_item_2_q'), a: t('faq_page_item_2_a') },
    { catKey: 'getting_started', q: t('faq_page_item_3_q'), a: t('faq_page_item_3_a') },
    { catKey: 'process', q: t('faq_page_item_4_q'), a: t('faq_page_item_4_a') },
    { catKey: 'process', q: t('faq_page_item_5_q'), a: t('faq_page_item_5_a') },
    { catKey: 'process', q: t('faq_page_item_6_q'), a: t('faq_page_item_6_a') },
    { catKey: 'process', q: t('faq_page_item_7_q'), a: t('faq_page_item_7_a') },
    { catKey: 'process', q: t('faq_page_item_8_q'), a: t('faq_page_item_8_a') },
    { catKey: 'plans_fees', q: t('faq_page_item_9_q'), a: t('faq_page_item_9_a') },
    { catKey: 'plans_fees', q: t('faq_page_item_10_q'), a: t('faq_page_item_10_a') },
    { catKey: 'plans_fees', q: t('faq_page_item_11_q'), a: t('faq_page_item_11_a') },
    { catKey: 'plans_fees', q: t('faq_page_item_12_q'), a: t('faq_page_item_12_a') },
    { catKey: 'eligibility', q: t('faq_page_item_13_q'), a: t('faq_page_item_13_a') },
    { catKey: 'eligibility', q: t('faq_page_item_14_q'), a: t('faq_page_item_14_a') },
    { catKey: 'eligibility', q: t('faq_page_item_15_q'), a: t('faq_page_item_15_a') },
    { catKey: 'eligibility', q: t('faq_page_item_16_q'), a: t('faq_page_item_16_a') },
    { catKey: 'eligibility', q: t('faq_page_item_17_q'), a: t('faq_page_item_17_a') },
    { catKey: 'compliance', q: t('faq_page_item_18_q'), a: t('faq_page_item_18_a') },
    { catKey: 'compliance', q: t('faq_page_item_19_q'), a: t('faq_page_item_19_a') },
    { catKey: 'compliance', q: t('faq_page_item_20_q'), a: t('faq_page_item_20_a') },
    { catKey: 'bilingual', q: t('faq_page_item_21_q'), a: t('faq_page_item_21_a') },
    { catKey: 'bilingual', q: t('faq_page_item_22_q'), a: t('faq_page_item_22_a') },
  ];

  const countByCategory = useMemo(() => {
    const counts: Record<string, number> = { all: faqs.length };
    faqs.forEach(({ catKey }) => {
      counts[catKey] = (counts[catKey] || 0) + 1;
    });
    return counts;
  }, [faqs.length]);

  const filtered = useMemo(() => {
    let list = activeCatKey === 'all' ? faqs : faqs.filter((f) => f.catKey === activeCatKey);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
    }
    return list;
  }, [activeCatKey, search, faqs.length]);

  const handleCatChange = (key: CategoryKey) => {
    setActiveCatKey(key);
    setOpenIdx(null);
    setSearch('');
  };

  useEffect(() => {
    document.title = 'FAQ | Honest Answers About Business Funding | Kailac';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', "Get honest answers about Kailac's expert-guided 0% APR business funding advisory. Eligibility, plans, fees, timeline, compliance, bilingual support — all explained clearly.");
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />

      {/* ── Hero ── */}
      <section className="bg-[#F8F3FB] pt-32 pb-16 px-6 lg:px-8 relative overflow-hidden">
        {/* decorative circles */}
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#E9D9F2]/40 pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-[#E9D9F2]/30 pointer-events-none" />
        <div className="max-w-3xl mx-auto text-center relative">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-4">{t('faq_page_eyebrow')}</p>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
            {t('faq_page_hero_title_1')}<br />
            <span className="text-[#4F2D63]">{t('faq_page_hero_title_2')}</span>
          </h1>
          <p className="text-base text-gray-500 max-w-xl mx-auto mb-8">{t('faq_page_hero_subtitle')}</p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <div className="w-5 h-5 flex items-center justify-center absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <i className="ri-search-line text-gray-400 text-base"></i>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOpenIdx(null); }}
              placeholder={t('faq_page_search_placeholder') || 'Search questions...'}
              className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-[#E9D9F2] bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#4F2D63] transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            )}
          </div>

          {/* Quick stats */}
          <div className="flex items-center justify-center gap-6 mt-8">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">22</p>
              <p className="text-xs text-gray-500">{t('faq_page_stat_questions') || 'Questions answered'}</p>
            </div>
            <div className="w-px h-8 bg-[#E9D9F2]" />
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">6</p>
              <p className="text-xs text-gray-500">{t('faq_page_stat_categories') || 'Categories'}</p>
            </div>
            <div className="w-px h-8 bg-[#E9D9F2]" />
            <div className="text-center">
              <p className="text-2xl font-bold text-[#4F2D63]">EN/ES</p>
              <p className="text-xs text-gray-500">{t('faq_page_stat_bilingual') || 'Bilingual support'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Category Cards ── */}
      <section className="py-10 px-6 lg:px-8 bg-white border-b border-[#E9D9F2]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {CATEGORY_KEYS.map((catKey) => {
              const meta = CATEGORY_META[catKey];
              const isActive = activeCatKey === catKey;
              return (
                <button
                  key={catKey}
                  onClick={() => handleCatChange(catKey)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer group ${
                    isActive
                      ? 'border-[#4F2D63] bg-[#F8F3FB]'
                      : 'border-[#E9D9F2] bg-white hover:border-[#DBB6EE] hover:bg-[#F8F3FB]/50'
                  }`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${isActive ? 'bg-[#4F2D63]' : meta.bg} transition-colors`}>
                    <i className={`${meta.icon} ${isActive ? 'text-white' : meta.accent} text-base`}></i>
                  </div>
                  <span className={`text-xs font-semibold text-center leading-tight ${isActive ? 'text-[#4F2D63]' : 'text-gray-600'}`}>
                    {categoryLabels[catKey]}
                  </span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? 'bg-[#4F2D63] text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {countByCategory[catKey] || 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FAQ Accordion ── */}
      <section className="py-14 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="lg:grid lg:grid-cols-12 lg:gap-10">

            {/* Left label column — desktop only */}
            <aside className="hidden lg:block lg:col-span-3 pt-1">
              <div className="sticky top-28 space-y-1">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                  {t('faq_page_cat_all')}
                </p>
                {CATEGORY_KEYS.filter((k) => k !== 'all').map((catKey) => {
                  const meta = CATEGORY_META[catKey];
                  const isActive = activeCatKey === catKey;
                  return (
                    <button
                      key={catKey}
                      onClick={() => handleCatChange(catKey)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors cursor-pointer group ${
                        isActive ? 'bg-[#F8F3FB] text-[#4F2D63]' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-7 h-7 flex items-center justify-center rounded-md ${isActive ? 'bg-[#E9D9F2]' : meta.bg} flex-shrink-0`}>
                        <i className={`${meta.icon} ${isActive ? 'text-[#4F2D63]' : meta.accent} text-sm`}></i>
                      </div>
                      <span className={`text-xs font-semibold leading-tight ${isActive ? 'text-[#4F2D63]' : ''}`}>
                        {categoryLabels[catKey]}
                      </span>
                      <span className="ml-auto text-xs text-gray-400 font-medium">
                        {countByCategory[catKey] || 0}
                      </span>
                    </button>
                  );
                })}
                <div className="mt-6 pt-6 border-t border-[#E9D9F2]">
                  <Link
                    to={lp('/contact', lang)}
                    className="flex items-center gap-2 text-xs text-[#4F2D63] font-semibold hover:text-[#3E234F] cursor-pointer"
                  >
                    <i className="ri-chat-3-line text-sm"></i>
                    {t('faq_page_still_cta_advisor')}
                  </Link>
                </div>
              </div>
            </aside>

            {/* Right accordion column */}
            <div className="lg:col-span-9">
              {/* Active category label */}
              {!search && (
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${CATEGORY_META[activeCatKey].bg}`}>
                    <i className={`${CATEGORY_META[activeCatKey].icon} ${CATEGORY_META[activeCatKey].accent} text-sm`}></i>
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900">{categoryLabels[activeCatKey]}</h2>
                    <p className="text-xs text-gray-400">{filtered.length} {t('faq_page_questions_label') || 'questions'}</p>
                  </div>
                </div>
              )}

              {search && (
                <div className="flex items-center gap-2 mb-6">
                  <i className="ri-search-line text-gray-400 text-sm"></i>
                  <p className="text-sm text-gray-600">
                    {filtered.length} {t('faq_page_results_for') || 'results for'}{' '}
                    <strong className="text-gray-900">&ldquo;{search}&rdquo;</strong>
                  </p>
                </div>
              )}

              {filtered.length === 0 && (
                <div className="text-center py-16 text-gray-400">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gray-50 mx-auto mb-4">
                    <i className="ri-question-mark text-2xl"></i>
                  </div>
                  <p className="text-base font-semibold text-gray-600 mb-1">
                    {t('faq_page_no_results') || 'No questions found'}
                  </p>
                  <p className="text-sm">{t('faq_page_try_search') || 'Try a different search or category'}</p>
                </div>
              )}

              <div className="space-y-2.5" itemScope itemType="https://schema.org/FAQPage">
                {filtered.map(({ q, a, catKey }, idx) => {
                  const meta = CATEGORY_META[catKey];
                  const isOpen = openIdx === idx;
                  return (
                    <div
                      key={`${catKey}-${idx}`}
                      className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                        isOpen
                          ? 'border-[#DBB6EE] bg-white'
                          : 'border-[#E9D9F2] bg-white hover:border-[#DBB6EE]'
                      }`}
                      itemScope itemProp="mainEntity" itemType="https://schema.org/Question"
                    >
                      <button
                        onClick={() => setOpenIdx(isOpen ? null : idx)}
                        className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                      >
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          {/* Category dot */}
                          <div className={`w-7 h-7 flex items-center justify-center rounded-md flex-shrink-0 mt-0.5 ${meta.bg}`}>
                            <i className={`${meta.icon} ${meta.accent} text-xs`}></i>
                          </div>
                          <span
                            className="text-sm font-semibold text-gray-900 leading-snug"
                            itemProp="name"
                          >
                            {q}
                          </span>
                        </div>
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${isOpen ? 'bg-[#4F2D63] rotate-45' : 'bg-gray-100 hover:bg-[#E9D9F2]'}`}>
                          <i className={`ri-add-line text-sm ${isOpen ? 'text-white' : 'text-gray-600'}`}></i>
                        </div>
                      </button>
                      <div
                        className={`transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
                        itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer"
                      >
                        <div className="px-5 pb-5 pl-[3.75rem]">
                          <p className="text-sm text-gray-600 leading-relaxed" itemProp="text">{a}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Still have questions ── */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Talk to advisor */}
            <div className="bg-[#4F2D63] rounded-2xl p-6 text-white flex flex-col">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3E234F] mb-4">
                <i className="ri-customer-service-2-line text-[#DBB6EE] text-lg"></i>
              </div>
              <h3 className="text-base font-bold mb-1">{t('faq_page_still_title')}</h3>
              <p className="text-xs text-white/60 mb-4 leading-relaxed flex-1">{t('faq_page_still_subtitle')}</p>
              <Link
                to={lp('/contact', lang)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-[#4F2D63] font-semibold text-xs hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap self-start"
              >
                {t('faq_page_still_cta_advisor')} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {/* WhatsApp */}
            <div className="bg-[#F0FDF9] rounded-2xl p-6 border border-emerald-100 flex flex-col">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#ECFDF5] mb-4">
                <i className="ri-whatsapp-line text-emerald-700 text-lg"></i>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">WhatsApp</h3>
              <p className="text-xs text-gray-500 mb-4 flex-1">{t('contact_quick_wa_desc')}</p>
              <a
                href={`https://wa.me/${KAILAC_WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#25D366] text-white font-semibold text-xs hover:bg-[#1EB455] transition-colors cursor-pointer whitespace-nowrap self-start"
              >
                <i className="ri-whatsapp-line"></i> {t('contact_wa_btn')}
              </a>
            </div>

            {/* View plans */}
            <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100 flex flex-col">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 mb-4">
                <i className="ri-price-tag-3-line text-amber-700 text-lg"></i>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">{t('plans_eyebrow')}</h3>
              <p className="text-xs text-gray-500 mb-4 flex-1">{t('plans_hero_subtitle')}</p>
              <Link
                to={lp('/plans', lang)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 text-white font-semibold text-xs hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap self-start"
              >
                {t('faq_page_still_cta_call')} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
